import {
  Component,
  computed,
  effect,
  input,
  OnInit,
  output,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { EmperorUIButtonTypes } from '@boldpenguin/emperor-presentational';
import {
  getFormId,
  getPartnerEngineConfig,
  isFormLoaded,
  isUserImpersonating,
} from '@boldpenguin/sdk';
import { LocalStorageService } from 'ngx-webstorage';
import {
  selectIntercomEnabled,
  selectTenantType,
  selectUserName,
} from 'src/app/core/services/bp-sdk-store-service/bp-sdk-store.selectors';
import { BpSdkStoreService } from 'src/app/core/services/bp-sdk-store-service/bp-sdk-store.service';
import {
  IntercomInternalLabels,
  IntercomService,
} from 'src/app/core/services/intercom-service/intercom.service';
import { ThemeService } from 'src/app/core/services/theme-service/theme.service';
import { TenantType } from 'src/app/features/settings/models/tenant.model';
import { externalUrls, localStorageKeys } from 'src/app/shared/constants';
import { applicationFormVisualizerUrl } from 'src/app/shared/utilities/debug-link-utilities';

@Component({
  selector: 'app-top-navigation-toolbar',
  templateUrl: './top-navigation-toolbar.component.html',
  styleUrls: ['./top-navigation-toolbar.component.scss'],
  standalone: false,
})
export class TopNavigationToolbarComponent implements OnInit {
  readonly suppressNavMenu = input<boolean>();
  readonly isSideNavOpen = input<boolean>(false);
  readonly isAlertBannerPresent = input<boolean>(false);
  readonly toggleSideNav = output<void>();

  readonly JiraCreateBugPage = externalUrls.JiraCreateBugPage;
  readonly uiButtonType = EmperorUIButtonTypes;
  intercomLabels = IntercomInternalLabels; // For setting the data-intercom-target attribute
  intercomActive = false;
  orderOfSelectedNav = 0;

  private sdkIsImpersonating = computed(() => {
    const sdkState = this.bpSdkStoreService.sdkReadyState();
    return !!sdkState && isUserImpersonating(sdkState);
  });
  loggedInUserIsImpersonating = computed(
    () =>
      this.sdkIsImpersonating() ||
      !!this.localStorageService.retrieve(localStorageKeys.impersonated),
  );
  nameOfUserBeingImpersonated = computed(() => {
    const sdkState = this.bpSdkStoreService.sdkReadyState();
    return !!sdkState ? selectUserName(sdkState) : '';
  });
  isExchangeTenantType = computed(() => {
    const sdkState = this.bpSdkStoreService.sdkReadyState();
    return !!sdkState && selectTenantType(sdkState) === TenantType.Exchange;
  });
  intercomEnabled = computed(() => {
    const sdkState = this.bpSdkStoreService.sdkReadyState();
    return !!sdkState && selectIntercomEnabled(sdkState);
  });
  isFormLoaded = computed(() => {
    const sdkState = this.bpSdkStoreService.sdkReadyState();
    return !!sdkState && isFormLoaded(sdkState);
  });
  formVisualizerUrl = computed(() => {
    const sdkState = this.bpSdkStoreService.sdkReadyState();
    if (sdkState) {
      const baseUri = getPartnerEngineConfig(sdkState).uri;
      const applicationFormId = getFormId(sdkState);

      if (baseUri && applicationFormId) {
        return applicationFormVisualizerUrl(baseUri, applicationFormId);
      }
    }
    return '';
  });
  logoPath = toSignal(this.themeService.getThemeLogoPath$(), {
    initialValue: '',
  });

  constructor(
    private bpSdkStoreService: BpSdkStoreService,
    private localStorageService: LocalStorageService,
    private intercomService: IntercomService,
    private themeService: ThemeService,
  ) {
    // Effect to store impersonation flag in localStorage
    effect(() => {
      if (this.sdkIsImpersonating()) {
        this.localStorageService.store(localStorageKeys.impersonated, 'true');
      }
    });
  }

  ngOnInit(): void {
    this.intercomService.onHide(this.onHideIntercom.bind(this));
    this.intercomService.onShow(this.onShowIntercom.bind(this));
  }

  sideNavClicked() {
    this.toggleSideNav.emit();
  }

  onHideIntercom() {
    this.intercomActive = false;
  }

  onShowIntercom() {
    this.intercomActive = true;
  }
}
